import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { legacyApi, CreditNote } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { Button } from '../../../components/ui/button';
import { Eye, Download } from 'lucide-react';
import { StandardActions } from '../components/StandardActions';

export const LegacyCreditNotes: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        status: 'all',
    });

    useEffect(() => {
        legacyApi.sales.listCreditNotes()
            .then(setCreditNotes)
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const token = state.q.trim().toLowerCase();
        return creditNotes.filter((cn) => {
            const matchesStatus = state.status === 'all' || String(cn.status_id) === String(state.status);
            if (!token) return matchesStatus;
            return matchesStatus && [
                cn.cn_number,
                cn.customer_name,
                cn.reason,
                cn.remarks,
                cn.status_name,
                `HC/INV/${String(cn.source_invoice_id).padStart(4, '0')}`,
            ].join(' ').toLowerCase().includes(token);
        });
    }, [creditNotes, state.q, state.status]);

    if (loading) return <div className="p-4">Loading credit notes...</div>;

    const columns = [
        { header: 'CN No', accessor: (cn: CreditNote) => cn.cn_number, className: 'font-mono whitespace-nowrap' },
        { header: 'Date', accessor: (cn: CreditNote) => cn.cn_date ? new Date(cn.cn_date).toLocaleDateString() : '-', className: 'whitespace-nowrap' },
        { header: 'Customer', accessor: (cn: CreditNote) => cn.customer_name || '-', className: 'min-w-[220px]' },
        { header: 'Source Invoice', accessor: (cn: CreditNote) => `HC/INV/${String(cn.source_invoice_id).padStart(4, '0')}`, className: 'font-mono whitespace-nowrap' },
        { header: 'Amount', accessor: (cn: CreditNote) => Number(cn.grand_total || 0).toFixed(2), className: 'text-right font-mono' },
        {
            header: 'Status',
            accessor: (cn: CreditNote) => (
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {cn.status_name}
                </span>
            ),
        },
        {
            header: 'Actions',
            accessor: (cn: CreditNote) => (
                <StandardActions
                    view={{
                        title: 'View Credit Note',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/legacy/credit-notes/${cn.cn_id}`);
                        },
                        icon: <Eye className="h-4 w-4 text-green-600" />,
                    }}
                    download={{
                        title: 'Print',
                        onClick: async (e) => {
                            e.stopPropagation();
                            await legacyApi.sales.downloadCreditNotePDF(cn.cn_id);
                        },
                        icon: <Download className="h-4 w-4 text-purple-600" />,
                    }}
                />
            ),
            className: 'w-28',
        },
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Credit Notes</h1>
                    <p className="text-muted-foreground">Track posted returns and source invoice linkage.</p>
                </div>
                <Button variant="outline" onClick={() => navigate('/legacy/invoices')}>Back to Invoices</Button>
            </div>
            <TableFilterBar
                searchValue={state.q}
                onSearchChange={(value) => setFilter('q', value)}
                searchPlaceholder="Search CN number, customer, invoice..."
                controls={[
                    {
                        key: 'status',
                        label: 'Status',
                        type: 'select',
                        value: state.status,
                        onChange: (value) => setFilter('status', value),
                        options: [
                            { value: 'all', label: 'All Statuses' },
                            { value: '1', label: 'Draft' },
                            { value: '2', label: 'Posted' },
                            { value: '3', label: 'Cancelled' },
                        ],
                    },
                ]}
                isDirty={isDirty}
                onClear={clearAll}
                rightSlot={`${filtered.length} results`}
            />
            <div className="overflow-auto rounded-md border bg-white shadow-sm">
                <CompactTable
                    data={filtered}
                    columns={columns}
                    keyField="cn_id"
                    onRowClick={(cn) => navigate(`/legacy/credit-notes/${cn.cn_id}`)}
                />
            </div>
        </div>
    );
};

